<template>
  <div class="w-full h-full mt-16 overflow-hidden">
    <div class="absolute mb-3 text-lg font-medium cursor-default top-3 has-tooltip">
      {{ label }}
      <span
        class="absolute top-0 left-0 z-50 inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
        {{ $t("For") }}:
        {{
          component_record
            ? connectTo == "this_record"
              ? `
          ${record_not_found ? $t("Not found") : $utils.getRecordName(component_record)}`
              : `${record_not_found ? $t("Record not found") : $utils.getRecordName(component_record)}`
            : record_not_found
              ? $t("Record Not Found")
              : $t("All Records")
        }}
      </span>
    </div>
    <div class="absolute left-0 flex p-2 bg-white top-10 dark:bg-dark-screen-background dark:border-dark-divider-border">
      <div class="relative self-stretch">
        <div class="flex p-1 rounded-lg group">
          <button
            type="button"
            class="flex rounded-md focus:outline-none"
            :tabindex="is_horizontal ? '0' : '-1'"
            @click="is_horizontal = true"
            @keydown.arrow-left="is_horizontal = false"
            @keydown.arrow-right="is_horizontal = false">
            <transition
              mode="out-in"
              enter-active-class="transition duration-100 ease-in"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100">
              <span
                v-if="is_horizontal"
                class="p-1.5 px-2 rounded-tl-md rounded-bl-md border border-blue flex items-center text-sm font-medium shadow-sm bg-blue">
                <Network class="w-4 h-4 rotate-90" aria-hidden="true" color="white" />
              </span>
              <span
                v-else
                class="p-1.5 px-2 border dark:border-dark-divider-border rounded-tl-md rounded-bl-md flex items-center text-sm font-medium shadow-sm">
                <Network class="w-4 h-4 rotate-90" aria-hidden="true" color="#899399" />
              </span>
            </transition>
          </button>
          <button
            type="button"
            class="flex rounded-md"
            :tabindex="!is_horizontal ? '0' : '-1'"
            @click="is_horizontal = false"
            @keydown.arrow-left="is_horizontal = false"
            @keydown.arrow-right="is_horizontal = false">
            <transition
              mode="out-in"
              enter-active-class="transition duration-100 ease-in"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100">
              <span
                v-if="!is_horizontal"
                class="p-1.5 px-2 rounded-tr-md rounded-br-md border border-blue flex items-center text-sm font-medium shadow-sm bg-blue">
                <Network class="w-4 h-4" aria-hidden="true" color="white" />
              </span>

              <span
                v-else
                class="p-1.5 px-2 border dark:border-dark-divider-border rounded-tr-md rounded-br-md flex items-center text-sm font-medium shadow-sm">
                <Network class="w-4 h-4" aria-hidden="true" color="#899399" />
              </span>
            </transition>
          </button>
        </div>
      </div>
      <div class="z-10 flex items-start flex-shrink-0 gap-1 mt-1 ml-2">
        <button
          type="button"
          class="inline-flex items-center p-1 text-gray-500 border-t border-b border-l shadow-sm rounded-tl-md rounded-bl-md dark:border-dark-divider-border hover:bg-white focus:outline-none"
          @click="map_zoom > 0 ? (map_zoom = map_zoom - 0.1) : null">
          <ZoomOut class="w-5 h-5" aria-hidden="true" />
        </button>
        <div class="p-1 px-2 -ml-1 text-sm border dark:border-dark-divider-border dark:text-common-grey">
          {{ Math.round(map_zoom * 100) }}
        </div>
        <button
          type="button"
          class="inline-flex items-center p-1 -ml-1 text-gray-500 border-t border-b border-r shadow-sm dark:border-dark-divider-border hover:bg-white focus:outline-none"
          @click="map_zoom = map_zoom + 0.1">
          <ZoomIn class="w-5 h-5" aria-hidden="true" />
        </button>

        <button
          type="button"
          class="inline-flex items-center p-1 -ml-1 text-gray-500 border-t border-b border-r shadow-sm rounded-tr-md rounded-br-md dark:border-dark-divider-border hover:bg-white focus:outline-none"
          @click="map_zoom = 1">
          <RotateCw class="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      <div v-if="!editing_disabled" class="p-1 ml-2">
        <div>
          <button
            type="button"
            :class="[
              `inline-flex items-center p-1 border rounded-md -ml-1 dark:border-dark-divider-border shadow-sm text-gray-500 ${
                is_map_edit_on ? 'hover:bg-green-300' : 'hover:bg-white'
              } focus:outline-none`
            ]">
            <Pencil v-if="!is_map_edit_on" class="w-5 h-5" aria-hidden="true" @click="is_map_edit_on = true" />
            <ClipboardList v-else class="w-5 h-5" aria-hidden="true" @click="is_map_edit_on = false" />
          </button>
        </div>
      </div>

      <div v-if="!editing_disabled" class="p-1 ml-1">
        <div>
          <button
            type="button"
            :class="[
              `inline-flex items-center p-1 border rounded-md -ml-1 dark:border-dark-divider-border shadow-sm text-gray-500 ${
                is_map_move_on ? 'bg-green-300' : 'bg-white'
              } focus:outline-none`
            ]">
            <Move
              :color="is_map_move_on ? 'white' : '#899399'"
              class="w-5 h-5"
              aria-hidden="true"
              @click="is_map_move_on = !is_map_move_on" />
          </button>
        </div>
      </div>
    </div>

    <div
      class="h-[calc(100%-90px)]"
      :class="[{ 'cursor-move no-scrollbar': is_map_move_on }, { 'overflow-scroll': !is_map_move_on }]"
      @wheel="hanldeWheel"
      @mousedown="startDragging"
      @mousemove="dragging"
      @mouseup="stopDragging"
      @mouseleave="stopDragging">
      <div ref="scrollContainer" class="flex items-center" :style="{ transform: 'translate(' + scrollX + 'px, ' + scrollY + 'px)' }">
        <div
          class="w-full flex z-50"
          :class="[{ 'pointer-events-none': is_map_move_on }]"
          @mousedown="startDragging"
          @mousemove="dragging"
          @mouseup="stopDragging"
          @mouseleave="stopDragging">
          <company-map
            v-if="company_map"
            :key="company_map.id + company_key"
            v-model:data="company_map"
            :props="{
              label: 'name',
              expand: 'expand',
              leafClass: 'leafClass',
              ghostClass: 'ghostItem',
              chosenClass: 'chosenItem',
              children: 'children',
              typeKey: 'type',
              key: 'id',
              onMove: checkIfMoveIsAllowed,
              onMoveEnd: onRecordDragEnd
            }"
            :horizontal="is_horizontal"
            :collapsable="true"
            class="min-h-100"
            :style="{ zoom: map_zoom }"
            @node-click="nodeClicked">
            <template #node="{ data }">
              <li
                v-if="data.type != 'contact'"
                :id="data.name"
                :class="{
                  'rounded-md': component_record.id == data.id
                }"
                class="min-w-max"
                @click.stop="openUpdateRecord(data)">
                <!-- if company card -->
                <div v-if="data.type == 'company'" class="bg-white rounded-lg">
                  <h2 id="company-overview-title" class="sr-only">
                    {{ $t("Company Overview") }}
                  </h2>
                  <div class="pb-1 bg-red-700 rounded-md dark:bg-dark-box-background">
                    <div class="sm:flex sm:items-center sm:justify-between bg-white px-1 rounded-sm">
                      <div class="items-center sm:flex sm:space-x-5 p-10">
                        <div class="p-0 rounded-md bg-light-brand-blue/10">
                          <img
                            onerror="this.style.display='none'"
                            class="inline-block object-contain rounded-md w-9 h-9"
                            :src="`https://logosnap.io/api/${$utils.getDomainName(
                              data.values[$utils.getOptionMetricReference('company', 'website')]
                            )}?token=ecoTB81m5dQqkrjw`"
                            alt="logo">
                        </div>
                        <div class="pt-1 mt-0 text-left">
                          <p class="text-sm font-medium dark:text-white text-grey-light-body" />
                          <p class="font-bold dark:text-white text-grey-light-body">
                            {{ Record.from(data).name }}
                          </p>
                          <p class="text-sm text-common-grey">
                            {{ Record.from(data)?.industry }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- if department card -->
                <div v-else>
                  <li
                    :style="{ backgroundColor: calcBgColor(data) }"
                    class="flex text-left bg-[#B171F9] text-white rounded-lg dark:bg-dark-box-background">
                    <div class="flex items-center p-2 pr-4">
                      <div class="p-3 bg-account-1/10 bg-white rounded-full">
                        <UsersRound class="w-5 h-5" color="#B171F9" />
                      </div>
                      <!-- <img class="flex-shrink-0 w-12 h-12 mx-auto opacity-50" :src="import(/* @vite-ignore */'@/assets/department-placeholder.png')" alt="" /> -->
                      <div class="ml-2">
                        <h3 class="font-medium text-gray-700">
                          {{ Record.from(data).name }}
                        </h3>
                        <div class="text-sm text-white">
                          {{ $t("Department") }}
                        </div>
                      </div>
                    </div>
                  </li>
                </div>

                <transition
                  mode="out-in"
                  enter-active-class="transition duration-300 ease-out transform "
                  enter-from-class="translate-y-5 opacity-0"
                  enter-to-class="translate-y-0 opacity-100">
                  <div>
                    <button
                      v-if="is_map_edit_on"
                      class="w-full m-0 mt-2 font-bold bg-white border cancel-btn dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white text-grey-light-body"
                      @click.stop="openAddNewRecord(data)">
                      <Plus class="w-5 h-5 mr-3" aria-hidden="true" /> {{ $t("Add") }}
                    </button>
                    <div v-if="is_map_edit_on" class="flex gap-1 mt-1">
                      <button
                        v-if="component_record.id != data.id"
                        class="w-full m-0 font-bold bg-white border cancel-btn dark:border-dark-divider-border dark:bg-dark-box-background dark:text-white text-grey-light-body"
                        @click.stop="openDeleteRecord(data)">
                        <Trash2 class="w-5 h-5 mr-3" aria-hidden="true" /> {{ $t("Delete") }}
                      </button>
                      <button
                        class="w-full m-0 font-bold bg-white border cancel-btn dark:border-dark-divider-border dark:bg-dark-box-background dark:text-white text-grey-light-body"
                        @click.stop="openUpdateRecord(data)">
                        <Pencil class="w-5 h-5 mr-3" aria-hidden="true" /> {{ $t("Edit") }}
                      </button>
                    </div>
                  </div>
                </transition>
              </li>
              <!-- if contact card -->
              <div
                v-else
                :style="{ backgroundColor: data.bg_color ? data.bg_color : '#b91c1c' }"
                class="rounded-lg pb-1"
                @mouseenter="handleNodeMouseEnter"
                @mouseleave="handleNodeMouseLeave">
                <div class="bg-white rounded-lg min-w-max dark:bg-dark-box-background" @click="openUpdateRecord(data)">
                  <div class="flex items-center justify-between w-full p-3 space-x-3 text-left border-b pb-5">
                    <div class="p-3 rounded-full bg-account-1/10">
                      <UserRound color="#B171F9" class="w-5 h-5" />
                    </div>
                    <div class="flex-1 truncate">
                      <div class="flex items-center">
                        <h3 class="text-sm font-medium truncate dark:text-white text-grey-light-body">
                          {{ Record.from(data).name }}

                          <div class="ml-4 relative inline-block cursor-pointer top-2" @click.stop="openModalNotes(data)">
                            <NotesIcon class="w-5 h-5 mr-2 hover:opacity-100" />
                            <div class="flex justify-center items-center absolute left-0.5 top-0">
                              <div class="flex items-center justify-center w-4 h-4 text-xs font-bold text-center text-white rounded-full">
                                <Plus v-if="!data.notes.length" class="w-5 h-5 transition" />
                                <div v-else>
                                  {{ data.notes.length }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </h3>
                      </div>
                      <p class="mt-1 text-sm text-gray-500 truncate">
                        {{ Record.from(data).email }}
                      </p>
                    </div>
                    <div class="w-fit -mt-7 flex">
                      <span v-for="i in 5" :key="i" :class="{ 'mr-1': i < 5 }">
                        <svg
                          class="block w-4 h-4"
                          :class="[Record.from(data).relationship_strength >= i ? 'text-blue' : 'text-gray-300']"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20">
                          <path
                            d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div class="mt-4 flex justify-between px-2 text-start">
                    <div class="text-common-grey text-xs flex flex-col space-y-2 pb-2 w-40">
                      <div class="flex space-x-1 items-center">
                        <span> <Mail class="w-3 h-3 mr-2" /> </span> {{ $t("Total emails") }}: {{ data.stats?.emails_count }}
                      </div>
                      <div class="flex space-x-1 items-center">
                        <span> <Phone class="w-3 h-3 mr-2" /> </span> {{ $t("Total calls") }}: {{ data.stats?.calls_count }}
                      </div>
                      <div class="flex space-x-1 items-center whitespace-nowrap">
                        <span> <LayoutPanelTop class="w-3 h-3 mr-2" /> </span> {{ data.stats?.goals_count }} {{ $t("goal(s)") }},
                        {{ data.stats?.interaction_count }} {{ $t("interaction(s)") }}, {{ data.stats?.plans_count }} {{ $t("plan(s)") }}
                      </div>
                      <div class="flex space-x-1 items-center">
                        <span> <Clock class="w-3 h-3 mr-2" /> </span>
                        {{ data.stats?.last_interaction_date ? $moment(data.stats?.last_interaction_date).format("DD MMM YYYY") : "N/A" }}
                      </div>
                    </div>
                    <div class="h-20 flex flex-col justify-center items-center" @click="handleSendEmail(data)">
                      <button class="btn cancel-btn mx-auto text-light-brand-blue border-light-brand-blue">{{ $t("Send Email") }}</button>
                    </div>
                  </div>
                </div>
                <transition
                  mode="out-in"
                  enter-active-class="transition duration-300 ease-out transform "
                  enter-from-class="translate-y-5 opacity-0"
                  enter-to-class="translate-y-0 opacity-100">
                  <div>
                    <button
                      v-if="is_map_edit_on"
                      class="w-full m-0 mt-1 font-bold bg-white border cancel-btn dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white text-grey-light-body"
                      @click.stop="openAddNewRecord(data)">
                      <Plus class="w-5 h-5 mr-3" aria-hidden="true" /> {{ $t("Add") }}
                    </button>
                    <div v-if="is_map_edit_on" class="flex gap-1 mt-1">
                      <button
                        class="w-full m-0 font-bold bg-white cancel-btn dark:border-dark-divider-border dark:bg-dark-box-background dark:text-white text-grey-light-body"
                        @click.stop="openDeleteRecord(data)">
                        <Trash2 class="w-5 h-5 mr-3" aria-hidden="true" /> {{ $t("Delete") }}
                      </button>
                      <button
                        class="w-full m-0 font-bold bg-white cancel-btn dark:border-dark-divider-border dark:bg-dark-box-background dark:text-white text-grey-light-body"
                        @click.stop="openUpdateRecord(data)">
                        <Pencil class="w-5 h-5 mr-3" aria-hidden="true" /> {{ $t("Edit") }}
                      </button>
                    </div>
                  </div>
                </transition>
              </div>
            </template>
          </company-map>
        </div>
      </div>
    </div>

    <div class="relative z-0 w-full h-full overflow-scroll text-center" />
    <AddNewRecord
      v-model:is-open="add_new_record_modal_is_open"
      :selected-record="selected_record"
      :page-reference="PAGE_REFERENCES.COMPANY_PROFILE_COMPANY_MAP"
      :object-type="selected_record?.type"
      :view-type="VIEW_TYPES.FORM_CREATE"
      @updateMap="
        () => {
          calcBgColor(selected_record)
          getRecordMap()
          add_new_record_modal_is_open = false
        }
      " />
    <UpdateRecord
      v-model:is-open="update_record_modal_is_open"
      :selected-record="selected_record"
      :page-reference="PAGE_REFERENCES.COMPANY_PROFILE_COMPANY_MAP"
      :object-type="selected_record?.type"
      :view-type="VIEW_TYPES.FORM_UPDATE"
      @updateMap="
        () => {
          getRecordMap()
          update_record_modal_is_open = false
        }
      " />
    <DeleteRecord
      v-model:is-open="delete_record_modal_is_open"
      :selected-record="selected_record"
      @updateMap="
        (r) => {
          getRecordMap()
          // deleteRecord(r)
          delete_record_modal_is_open = false
        }
      " />
  </div>
</template>
<script>
import { useLayoutItemSharedLogic } from "@/components/shared/pages/useLayoutItemSharedLogic.js"
import NotesIcon from "@/components/shared/icons/NotesIcon.vue"
import dataService from "@/services/data.service"
import { Record } from "@/models/record"
import {
  UsersRound,
  Move,
  Network,
  Clock,
  LayoutPanelTop,
  Phone,
  Mail,
  Plus,
  Trash2,
  RotateCw,
  Pencil,
  ClipboardList,
  ZoomIn,
  ZoomOut,
  UserRound
} from "lucide-vue-next"
import AddNewRecord from "./AddNewRecord"
import UpdateRecord from "./UpdateRecord"
import DeleteRecord from "./DeleteRecord"
import { VIEW_TYPES, PAGE_REFERENCES } from "@/store/constants/references"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { utils } from "@/store/helpers"

export default {
  name: "CompanyMapView",
  components: {
    Move,
    NotesIcon,
    DeleteRecord,
    AddNewRecord,
    UpdateRecord,
    ZoomIn,
    ZoomOut,
    RotateCw,
    Trash2,
    Pencil,
    ClipboardList,
    Plus,
    Network,
    UsersRound,
    UserRound,
    Mail,
    Phone,
    LayoutPanelTop,
    Clock
  },

  inject: ["canUserEditLayout"],

  props: {
    label: null,
    linkedRecord: null,
    connectTo: null
  },
  data() {
    return {
      loading: false,
      record_error: false,
      component_record: null,
      is_dragging: false,
      is_dragging_blocked: false,
      startX: 0,
      scrollX: 0,
      startY: 0,
      scrollY: 0,
      Record,
      add_new_record_modal_is_open: false,
      update_record_modal_is_open: false,
      delete_record_modal_is_open: false,
      selected_record: { label: "", id: null, type: "company" },
      is_horizontal: false,

      company_map: null,
      company_key: 1,
      map_zoom: 1,
      is_map_edit_on: false,
      is_map_move_on: false,
      PAGE_REFERENCES,
      VIEW_TYPES,
      record_not_found: false,
      departments_bg_colors: [
        "#B171F9",
        "#27C884",
        "#FFBE15",
        "#2DA7D7",
        "#B171F9",
        "#27C884",
        "#FFBE15",
        "#2DA7D7",
        "#B171F9",
        "#27C884",
        "#FFBE15",
        "#2DA7D7",
        "#B171F9",
        "#27C884",
        "#FFBE15",
        "#2DA7D7",
        "#B171F9",
        "#27C884",
        "#FFBE15",
        "#2DA7D7"
      ],
      department_parents: {}
    }
  },

  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  created() {
    const { loading, record_error, component_record } = useLayoutItemSharedLogic(
      this.$props,
      this.postGetComponentRecord,
      this.registerListeners,
      this.unregisterListeners
    )
    this.loading = loading
    this.record_error = record_error
    this.component_record = component_record
    this.getRecordMap()
    this.$emitter.emit(ALL_EMITTERS.CURRENT_COMPANY_CHANGED_INSIDE_MAP)
  },
  methods: {
    postGetComponentRecord() {
      this.getRecordMap()
      this.$emitter.emit(ALL_EMITTERS.CURRENT_COMPANY_CHANGED_INSIDE_MAP)
    },

    // Custom listener for this component
    registerListeners() {
      this.$emitter.on(ALL_EMITTERS.UPDATE_MAP, () => {
        this.getRecordMap()
      })
      this.$emitter.on(ALL_EMITTERS.CURRENT_COMPANY_CHANGED_INSIDE_MAP, () => {
        this.getRecordMap()
      })
    },
    unregisterListeners() {
      this.$emitter.off(ALL_EMITTERS.UPDATE_MAP)
      this.$emitter.off(ALL_EMITTERS.CURRENT_COMPANY_CHANGED_INSIDE_MAP)
    },
    openModalNotes(record) {
      this.$emitter.emit(ALL_EMITTERS.ADD_NOTES_RECORD, { company: record })
    },
    calcBgColor(data) {
      let color_idx = 0
      if (this.department_parents[data.parent]) {
        if (this.department_parents[data.parent].findIndex((id) => id == data.id) !== -1) {
          color_idx = this.department_parents[data.parent].findIndex((id) => id == data.id)
        } else {
          color_idx = this.department_parents[data.parent].length
          this.department_parents[data.parent].push(data.id)
        }
      } else {
        this.department_parents[data.parent] = []
      }

      this.department_parents[data.parent]
      data.bg_color = this.departments_bg_colors[color_idx]
      data.color_idx = color_idx
      data.children.forEach((child) => {
        child.bg_color = this.departments_bg_colors[color_idx]
      })
      return this.departments_bg_colors[color_idx]
    },
    hanldeWheel(e) {
      if (this.is_map_move_on) {
        e.preventDefault()

        this.map_zoom = this.map_zoom - e.deltaY / 1920
        if (this.map_zoom < 0) {
          this.map_zoom = 0
        }
      }
    },
    handleNodeMouseEnter() {
      this.is_dragging_blocked = true
    },

    handleNodeMouseLeave() {
      this.is_dragging_blocked = false
    },

    startDragging(event) {
      if (this.is_map_move_on) {
        this.is_dragging = true
        this.startX = event.pageX - this.scrollX
        this.startY = event.pageY - this.scrollY
      }
    },
    dragging(event) {
      if (!this.is_dragging) {
        return
      }
      const mouseX = event.pageX
      const mouseY = event.pageY
      this.scrollX = mouseX - this.startX
      this.scrollY = mouseY - this.startY
    },
    stopDragging() {
      this.is_dragging = false
    },
    handleSendEmail(data) {
      setTimeout(() => {
        this.update_record_modal_is_open = false
      }, 10)

      if (this.$store.state.data.records_fetched[data.id]) {
        const i_service = this.$store.state.integration.integrations.find((i) => i.name == "gmail")
        if (i_service && i_service.scope.includes("https://www.googleapis.com/auth/gmail.modify")) {
          if (i_service.status == "on") {
            this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
              records: [],
              record: this.$store.state.data.records_fetched[data.id],
              object_type: this.$store.state.data.records_fetched[data.id].type
            })
          } else {
            this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
              message: this.$t("Email integration is not active, Activate integration to send an email")
            })
          }
        } else {
          this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
            message: this.$t("In order to send an email please link your account to your Email.")
          })
        }
      } else {
        this.$store.dispatch("data/getRecord", data.id).then((res) => {
          const i_service = this.$store.state.integration.integrations.find((i) => i.name == "gmail")
          if (i_service && i_service.scope.includes("https://www.googleapis.com/auth/gmail.modify")) {
            if (i_service.status == "on") {
              this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, { records: [], record: res.data, object_type: res.data.type })
            } else {
              this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                message: this.$t("Email integration is not active, Activate integration to send an email")
              })
            }
          } else {
            this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
              message: this.$t("In order to send an email please link your account to your Email.")
            })
          }
        })
      }
    },
    deleteRecord(record = null, removed = false) {
      if (!removed) {
        if (record == null) {
          record = this.company_map
        }
        if (record?.children?.length > 0) {
          record.children.forEach((rec, index) => {
            if (rec.id == this.selected_record.id) {
              record.children.splice(index, 1)
              removed = true
            }
            if (index == record.children.length - 1 && !removed) {
              record.children.forEach((rec) => {
                this.deleteRecord(rec, removed)
              })
            }
          })
        }
      }
    },
    openAddNewRecord(data) {
      this.selected_record = data
      this.add_new_record_modal_is_open = true
    },
    openUpdateRecord(data) {
      this.selected_record = data
      this.update_record_modal_is_open = true
    },
    openDeleteRecord(data) {
      this.selected_record = data
      this.delete_record_modal_is_open = true
    },
    getRecordMap() {
      dataService.getRecordMap(this.component_record?.id).then((result) => {
        this.company_map = this.$utils.clone(result.data)
        if (!this.company_map.children.length) {
          this.scrollX = 700
        }
        this.selected_record = null
        this.add_new_record_modal_is_open = false
        this.update_record_modal_is_open = false
        this.delete_record_modal_is_open = false
        this.company_key = this.company_key + 1
      })
    },
    checkIfMoveIsAllowed(evt) {
      if (utils.isEmpty(evt)) {
        this.is_dragging_blocked = true
        return true
      } else {
        let record_type = evt?.draggedContext?.element?.type
        let to_parent_type = evt?.to?.offsetParent?.dataset?.type

        let is_allowed_move = false
        if (record_type == "contact") {
          is_allowed_move = true
        } else if (record_type == "department" && (to_parent_type == "company" || to_parent_type == "department")) {
          is_allowed_move = true
        } else if (record_type == "company" && to_parent_type == "company") {
          is_allowed_move = true
        }

        this.is_dragging_blocked = is_allowed_move
        return is_allowed_move
      }
    },
    onRecordDragEnd(evt) {
      this.is_dragging = false
      this.is_dragging_blocked = false
      if (!this.$utils.isEmpty(evt)) {
        let record_type = evt.draggedContext.element.type
        let record_id = evt.draggedContext.element.id
        let to_parent_type = evt.to.offsetParent.dataset.type || evt.to.dataset.type
        let to_parent_id = evt.to.offsetParent.dataset.id || evt.to.dataset.id
        let allowed = false
        if (record_type == "contact") {
          allowed = true
        } else if (record_type == "department" && (to_parent_type == "company" || to_parent_type == "department")) {
          allowed = true
        } else if (record_type == "company" && to_parent_type == "company") {
          this.is_dragging = false

          allowed = true
        }
        if (allowed) {
          this.$store.dispatch("data/updateRecord", { record_id: record_id, data: { parent: to_parent_id } }).then(() => {
            this.getRecordMap()
          })

          return true
        }
      }

      return false
    },
    nodeClicked() {
      //node clicked
    }
  }
}
</script>

<style>
.org-tree-container {
  background-color: transparent !important;
}
.org-tree-node-label-inner {
  padding: 0px !important;
}

.leafClass {
  border-width: 1px;
  background-color: #0d8aba;
  opacity: 0.1;
  min-width: 100px;
  min-height: 100px;
  height: 150px;
  transition: all 0.35s;
}

#HiddenNode {
  display: none;
}
.ghostItem {
  background-color: grey;
  border-width: 0px;
}
.chosenItem {
  background-color: transparent;
  border-width: 0px;
}
.ghost {
  @apply bg-gray-900 border-0;
}
.chosen {
  @apply bg-gray-700 border-0;
}

.horizontal .organization-tree-node {
  display: table !important;
}
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
